import { useState } from "react";
import { fmtCFA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock, Unlock, RotateCcw } from "lucide-react";

export function CaisseHeader({ data, caisseOuverte, canManage, busy, onOpen, onClose, onReopen }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Caisse</h1>
        <p className="text-sm text-muted-foreground mt-1">Point de vente — {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</p>
      </div>
      <div className="flex items-center gap-3">
        {caisseOuverte ? (
          <>
            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40 px-3 py-1.5" data-testid="caisse-status-badge">
              <Unlock className="w-3.5 h-3.5 mr-1.5" /> Caisse ouverte — Fond : {fmtCFA(data.caisse.montant_ouverture)}
            </Badge>
            {canManage && (
              <Button variant="outline" data-testid="fermer-caisse-button" className="border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10" onClick={onClose}>
                <Lock className="w-4 h-4 mr-2" /> Clôturer la caisse
              </Button>
            )}
          </>
        ) : (
          <>
            <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/40 px-3 py-1.5" data-testid="caisse-status-badge">
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Caisse fermée
            </Badge>
            {!data.caisse && canManage && (
              <Button data-testid="ouvrir-caisse-button" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={onOpen}>
                <Unlock className="w-4 h-4 mr-2" /> Ouvrir la caisse
              </Button>
            )}
            {data.caisse && canManage && (
              <Button data-testid="rouvrir-caisse-button" disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" onClick={onReopen}>
                <RotateCcw className="w-4 h-4 mr-2" /> Rouvrir la caisse
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function ResumeJour({ resume }) {
  const cards = [
    { label: "Total Espèces", value: fmtCFA(resume.total_especes), testid: "resume-especes" },
    { label: "Total Wave", value: fmtCFA(resume.total_wave), testid: "resume-wave" },
    { label: "Total Orange Money", value: fmtCFA(resume.total_orange_money), testid: "resume-orange-money" },
    { label: "Total Ventes", value: fmtCFA(resume.total), testid: "resume-total" },
    { label: "Transactions", value: resume.nb_ventes, testid: "resume-nb" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="resume-jour">
      {cards.map((r) => (
        <Card key={r.label} className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{r.label}</p>
            <p className="text-xl font-bold text-foreground mt-1" data-testid={r.testid}>{r.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function OuvrirDialog({ open, busy, onClose, onConfirm }) {
  const [montant, setMontant] = useState("");
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground">
        <DialogHeader><DialogTitle data-testid="ouvrir-caisse-dialog-title">Ouvrir la caisse du jour</DialogTitle></DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="text-foreground/80">Fond de caisse (montant d'ouverture)</Label>
          <Input type="number" min="0" value={montant} onChange={(e) => setMontant(e.target.value)} placeholder="Ex : 5000" data-testid="montant-ouverture-input" className="bg-background border-border text-foreground" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-border text-foreground/80">Annuler</Button>
          <Button onClick={() => onConfirm(montant)} disabled={busy} data-testid="confirm-ouvrir-caisse" className="bg-emerald-500 hover:bg-emerald-600 text-white">Ouvrir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RouvrirDialog({ open, data, busy, onClose, onConfirm }) {
  const [motif, setMotif] = useState("");
  const c = data.caisse || {};
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground">
        <DialogHeader><DialogTitle data-testid="rouvrir-caisse-dialog-title">Rouvrir la caisse du jour</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <p className="text-foreground/80">
            La caisse a été clôturée par <strong>{c.fermee_par}</strong>
            {c.fermee_le && <> à {new Date(c.fermee_le).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</>}
            {" "}avec un total de ventes de <strong>{fmtCFA(data.resume.total)}</strong>.
          </p>
          <p className="text-amber-600 text-xs">La réouverture annule la clôture : vous devrez clôturer à nouveau en fin de journée. Cette action est enregistrée dans le journal.</p>
          <div className="space-y-2">
            <Label className="text-foreground/80">Motif de la réouverture (optionnel)</Label>
            <Input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex : vente oubliée, erreur de clôture…" data-testid="motif-reouverture-input" className="bg-background border-border text-foreground" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-border text-foreground/80">Annuler</Button>
          <Button onClick={() => onConfirm(motif)} disabled={busy} data-testid="confirm-rouvrir-caisse" className="bg-amber-500 hover:bg-amber-600 text-white">
            <RotateCcw className="w-4 h-4 mr-2" /> Rouvrir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FermerDialog({ open, data, busy, onClose, onConfirm }) {
  const fond = data.caisse?.montant_ouverture || 0;
  const lignes = [
    ["Fond de caisse", fond], ["Ventes espèces", data.resume.total_especes],
    ["Ventes Wave", data.resume.total_wave], ["Ventes Orange Money", data.resume.total_orange_money],
  ];
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground">
        <DialogHeader><DialogTitle data-testid="fermer-caisse-dialog-title">Clôturer la caisse du jour</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          {lignes.map(([l, v]) => <div key={l} className="flex justify-between text-foreground/80"><span>{l}</span><span>{fmtCFA(v)}</span></div>)}
          <div className="flex justify-between font-bold text-emerald-600 dark:text-emerald-400 border-t border-border pt-3">
            <span>Total en caisse (espèces)</span>
            <span data-testid="cloture-total-especes">{fmtCFA(fond + data.resume.total_especes)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-border text-foreground/80">Annuler</Button>
          <Button onClick={onConfirm} disabled={busy} data-testid="confirm-fermer-caisse" className="bg-red-500 hover:bg-red-600 text-white">Clôturer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
